import React, { useState, useEffect } from 'react';
import { adminAPI } from '../services/api';
import {
  Shield,
  Search,
  Plus,
  AlertTriangle,
  Globe,
  Clock,
  Ban,
  CheckCircle
} from 'lucide-react';
import { toast } from 'react-toastify';

const AdminBlockedIPs = () => {
  const [blockedIPs, setBlockedIPs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0
  });

  const fetchBlockedIPs = React.useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        search: searchTerm || undefined
      };

      const response = await adminAPI.getBlockedIps(params);
      setBlockedIPs(response.data.data.blockedIps || []);
      setPagination(prev => ({
        ...prev,
        total: response.data.data.pagination.total,
        totalPages: response.data.data.pagination.totalPages
      }));
    } catch (error) {
      console.error('Error fetching blocked IPs:', error);
      toast.error('Failed to fetch blocked IPs');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, searchTerm]);

  useEffect(() => {
    fetchBlockedIPs();
  }, [fetchBlockedIPs]);

  const handleBlockIP = async (ipData) => {
    try {
      const response = await adminAPI.blockIp(ipData);
      setBlockedIPs([response.data.data.blockedIP, ...blockedIPs]);
      setPagination(prev => ({ ...prev, total: prev.total + 1 }));
      setShowAddModal(false);
      toast.success('IP address blocked successfully');
    } catch (error) {
      console.error('Error blocking IP:', error);
      toast.error(error.response?.data?.message || 'Failed to block IP address');
    }
  };

  const handleUnblockIP = async (ipAddress) => {
    if (!window.confirm(`Are you sure you want to unblock ${ipAddress}?`)) {
      return;
    }

    try {
      await adminAPI.unblockIp(ipAddress);
      setBlockedIPs((prev) => (prev || []).filter(ip => ip.ipAddress !== ipAddress));
      setPagination(prev => ({ ...prev, total: prev.total - 1 }));
      toast.success('IP address unblocked successfully');
    } catch (error) {
      console.error('Error unblocking IP:', error);
      toast.error(error.response?.data?.message || 'Failed to unblock IP address');
    }
  };

  const filteredIPs = (blockedIPs || []).filter(ip => {
    const matchesSearch = !searchTerm ||
      ip.ipAddress.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (ip.reason && ip.reason.toLowerCase().includes(searchTerm.toLowerCase()));

    return matchesSearch;
  });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const getBlockDuration = (blockedAt, expiresAt) => {
    if (!expiresAt) return 'Permanent';
    
    const now = new Date();
    const expires = new Date(expiresAt);
    
    if (expires < now) return 'Expired';
    
    const diffMs = expires - now;
    const diffHours = Math.ceil(diffMs / (1000 * 60 * 60));
    
    if (diffHours < 24) {
      return `${diffHours} hour${diffHours === 1 ? '' : 's'} remaining`;
    }
    
    const diffDays = Math.ceil(diffHours / 24);
    return `${diffDays} day${diffDays === 1 ? '' : 's'} remaining`;
  };

  if (loading && blockedIPs.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <Shield className="w-8 h-8 mr-3 text-red-600" />
              IP Blocking Management
            </h1>
            <p className="text-gray-600 mt-2">
              Manage blocked IP addresses and security rules
            </p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="btn btn-primary btn-md"
          >
            <Plus className="w-4 h-4 mr-2" />
            Block IP Address
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="card p-4">
          <div className="flex items-center">
            <Ban className="w-6 h-6 text-red-600 mr-2" />
            <div>
              <p className="text-sm text-gray-600">Total Blocked</p>
              <p className="text-xl font-bold">{pagination.total}</p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center">
            <Clock className="w-6 h-6 text-orange-600 mr-2" />
            <div>
              <p className="text-sm text-gray-600">Temporary Blocks</p>
              <p className="text-xl font-bold">
                {(blockedIPs || []).filter(ip => ip.expiresAt && new Date(ip.expiresAt) > new Date()).length}
              </p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center">
            <AlertTriangle className="w-6 h-6 text-yellow-600 mr-2" />
            <div>
              <p className="text-sm text-gray-600">Permanent Blocks</p>
              <p className="text-xl font-bold">
                {(blockedIPs || []).filter(ip => !ip.expiresAt).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="card p-6 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search by IP address or reason..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input-field pl-10"
          />
        </div>
      </div>

      {/* Blocked IPs Table */}
      <div className="card overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium">Blocked IP Addresses ({filteredIPs.length})</h3>
        </div>
        
        {filteredIPs.length === 0 ? (
          <div className="text-center py-12">
            <Shield className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Blocked IPs</h3>
            <p className="text-gray-600 mb-6">
              {searchTerm ? 'No IPs match your search.' : 'No IP addresses are currently blocked.'}
            </p>
            {!searchTerm && (
              <button
                onClick={() => setShowAddModal(true)}
                className="btn btn-primary btn-md"
              >
                <Plus className="w-4 h-4 mr-2" />
                Block First IP
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    IP Address
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Reason
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Blocked By
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Duration
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Blocked At
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredIPs.map((ip) => {
                  const isExpired = ip.expiresAt && new Date(ip.expiresAt) < new Date();
                  
                  return (
                    <tr key={ip.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Globe className="w-4 h-4 text-gray-400 mr-2" />
                          <code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
                            {ip.ipAddress}
                          </code>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {ip.reason || 'No reason provided'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {ip.blockedByUsername || 'System'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm">
                          {getBlockDuration(ip.createdAt, ip.expiresAt)}
                        </div>
                        {ip.expiresAt && (
                          <div className="text-xs text-gray-500">
                            Expires: {formatDate(ip.expiresAt)}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(ip.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          isExpired
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {isExpired ? 'Expired' : 'Active'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleUnblockIP(ip.ipAddress)}
                          className="btn btn-icon-sm btn-soft-success"
                          title="Unblock IP"
                        >
                          <CheckCircle className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} results
            </div>
            <div className="flex space-x-1">
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page === 1}
                className="btn btn-sm btn-outline-secondary"
              >
                Previous
              </button>
              {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                const page = i + 1;
                return (
                  <button
                    key={page}
                    onClick={() => setPagination(prev => ({ ...prev, page }))}
                    className={`btn btn-sm ${
                      pagination.page === page
                        ? 'btn-primary'
                        : 'btn-outline-secondary'
                    }`}
                  >
                    {page}
                  </button>
                );
              })}
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page === pagination.totalPages}
                className="btn btn-sm btn-outline-secondary"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Block IP Modal */}
      {showAddModal && (
        <BlockIPModal
          onClose={() => setShowAddModal(false)}
          onSubmit={handleBlockIP}
        />
      )}
    </div>
  );
};

// Block IP Modal Component
const BlockIPModal = ({ onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    ipAddress: '',
    reason: '',
    duration: 'permanent', // permanent, 1h, 24h, 7d, 30d
    customHours: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    let expiresAt = null;
    
    if (formData.duration !== 'permanent') {
      const now = new Date();
      let hours;
      
      switch (formData.duration) {
        case '1h':
          hours = 1;
          break;
        case '24h':
          hours = 24;
          break;
        case '7d':
          hours = 24 * 7;
          break;
        case '30d':
          hours = 24 * 30;
          break;
        case 'custom':
          hours = parseInt(formData.customHours);
          if (!hours || hours <= 0) {
            toast.error('Please enter a valid number of hours');
            return;
          }
          break;
        default:
          hours = 24;
      }
      
      expiresAt = new Date(now.getTime() + (hours * 60 * 60 * 1000));
    }

    const data = {
      ipAddress: formData.ipAddress.trim(),
      reason: formData.reason.trim() || undefined,
      expiresAt: expiresAt ? expiresAt.toISOString() : undefined
    };
    
    onSubmit(data);
  };

  const validateIP = (ip) => {
    const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
    return ipRegex.test(ip);
  };

  const isValidForm = formData.ipAddress.trim() && validateIP(formData.ipAddress.trim()) &&
    (formData.duration !== 'custom' || (formData.customHours && parseInt(formData.customHours) > 0));

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h3 className="text-lg font-medium mb-4 flex items-center">
          <Shield className="w-5 h-5 mr-2 text-red-600" />
          Block IP Address
        </h3>
        
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              IP Address *
            </label>
            <input
              type="text"
              value={formData.ipAddress}
              onChange={(e) => setFormData({...formData, ipAddress: e.target.value})}
              className={`input-field ${
                formData.ipAddress && !validateIP(formData.ipAddress) ? 'border-red-300' : ''
              }`}
              placeholder="192.168.1.1"
              required
            />
            {formData.ipAddress && !validateIP(formData.ipAddress) && (
              <p className="text-red-600 text-xs mt-1">Please enter a valid IP address</p>
            )}
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reason (optional)
            </label>
            <textarea
              value={formData.reason}
              onChange={(e) => setFormData({...formData, reason: e.target.value})}
              className="input-field"
              placeholder="Suspicious activity, spam, etc."
              rows="3"
            />
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Block Duration
            </label>
            <select
              value={formData.duration}
              onChange={(e) => setFormData({...formData, duration: e.target.value})}
              className="input-field"
            >
              <option value="1h">1 Hour</option>
              <option value="24h">24 Hours</option>
              <option value="7d">7 Days</option>
              <option value="30d">30 Days</option>
              <option value="custom">Custom Duration</option>
              <option value="permanent">Permanent</option>
            </select>
          </div>

          {formData.duration === 'custom' && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Custom Hours
              </label>
              <input
                type="number"
                value={formData.customHours}
                onChange={(e) => setFormData({...formData, customHours: e.target.value})}
                className="input-field"
                placeholder="24"
                min="1"
                required
              />
            </div>
          )}

          {formData.duration === 'permanent' && (
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
              <div className="flex items-start">
                <AlertTriangle className="w-4 h-4 text-yellow-600 mr-2 mt-0.5" />
                <p className="text-sm text-yellow-800">
                  This IP will be permanently blocked until manually unblocked.
                </p>
              </div>
            </div>
          )}
          
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary btn-md"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-danger btn-md"
              disabled={!isValidForm}
            >
              <Shield className="w-4 h-4 mr-2" />
              Block IP
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminBlockedIPs;